<template>
	<StandardModal
		open
		:title="$t('admin_groups_manage_permissions_button')"
		:confirmText="$t('common_button_save')"
		:confirmProps="{ disabled, loading }"
		@confirm="save"
		@cancel="close"
		ref="modal"
		class="permissions-modal"
	>
		<div class="modal-wrapper">
			<GroupPermissions embedded :chat="adaptedGroup" v-model="settings" />
		</div>
	</StandardModal>
</template>

<script>
import GroupPermissions from '@/components/GroupSettings/GroupPermissions'
import { StandardModal } from '@/components/common'
import { patch } from '../common'

export default {
	name: 'PermissionsModal',
	components: { StandardModal, GroupPermissions },
	props: { group: Object },
	data() {
		return { settings: {}, loading: false }
	},
	emits: ['success', 'close'],

	mounted() {
		this.copySettings()
	},

	computed: {
		// Adapt group to chat format expected by GroupPermissions
		adaptedGroup() {
			if (!this.group) return null
			return {
				id: this.group.id,
				...this.convertGrantsToProps(this.group.grants),
				...this.group.permissions,
			}
		},

		disabled() {
			return !this.hasChanges
		},

		hasChanges() {
			if (!this.group?.permissions) return false
			return Object.keys(this.settings).some(
				(key) => this.settings[key] !== this.group.permissions[key]
			)
		},
	},

	methods: {
		convertGrantsToProps(grants) {
			// Convert { update_title: true } to { grant_update_title: true }
			return Object.entries(grants || {}).reduce((acc, [key, value]) => {
				acc[`grant_${key}`] = value
				return acc
			}, {})
		},

		copySettings() {
			if (!this.group?.permissions) return
			this.settings = { ...this.group.permissions }
		},

		async save() {
			this.loading = true
			try {
				// Only send permissions the owner is allowed to change (grant
				// true). A grant of false means the server will reject the
				// permission with MISSING_GRANT — e.g. a canvas-locked group.
				const grants = this.group?.grants || {}
				const permissions = Object.fromEntries(
					Object.entries(this.settings).filter(([key]) => grants[key])
				)
				const group = await patch(`/chats/${this.group.id}`, {
					permissions,
				})
				this.$emit('success', group)
				this.$alert.success(
					this.$t('admin_people_changes_saved_confirmation_label')
				)
				this.close()
			} catch (e) {
				this.loading = false
				this.$refs.modal.$alert.error(this.$t('common_api_error_save'))
			}
		},

		close() {
			this.$emit('close')
		},
	},

	watch: {
		group() {
			this.copySettings()
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.permissions-modal {
	.modal-wrapper {
		padding: 24px 35px;

		:deep(.permission-container) {
			flex-direction: column;
			align-items: flex-start;
			gap: 3px;

			.label-tip label {
				font-family: Avenir-Black;
				font-size: 14px;
				color: var(--neutral-800);
			}

			.help-icon {
				font-size: 16px;
				cursor: help;
			}

			.permission-dropdown {
				width: 100%;
			}
		}
	}
}
</style>
